import { type FC, type ReactNode, useState } from "react";

import { Mail } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { FieldError } from "@/shared/components/ui/FieldError";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useRequestDocument } from "@/shared/hooks/useRequestDocument";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { isValidEmail } from "@/shared/utils/validators";

import { ModalStepLayout } from "./ModalStepLayout";

interface TriageModalRequestStepProps {
  title: string;
  description: string;
  sidebar: ReactNode;
  documentType: string;
  documentLabel: string;
  personFirstName: string;
  onBack: () => void;
  onRequestSent: (recipientName: string, recipientRelationship: string | null) => void;
}

export const TriageModalRequestStep: FC<TriageModalRequestStepProps> = ({
  title,
  description,
  sidebar,
  documentType,
  documentLabel,
  personFirstName,
  onBack,
  onRequestSent,
}) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { createContactAsync, isCreating } = useUserContacts();
  const requestDocument = useRequestDocument();

  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const personName = personFirstName || "the person who passed";
  const userFirstName = user?.first_name || "Someone";
  const canSend = name.trim() !== "" && isValidEmail(email.trim());
  const isBusy = isCreating || requestDocument.isPending;

  const handleSend = async () => {
    if (!canSend || !estateId || !user) return;
    setError(null);

    try {
      const nameParts = name.trim().split(/\s+/);
      const contact = await createContactAsync({
        first_name: nameParts[0] ?? null,
        last_name: nameParts.length > 1 ? nameParts.slice(1).join(" ") : null,
        email: email.trim(),
        relationship: relationship || null,
      });

      const requesterName = [user.first_name, user.last_name].filter(Boolean).join(" ");

      requestDocument.mutate(
        {
          estateId,
          documentType,
          contactId: contact.id,
          notification: {
            recipient_email: email.trim(),
            requester_name: requesterName || "Someone",
            inviter_first_name: userFirstName,
            invitee_first_name: contact.first_name || "",
            decedent_name: personName,
            document_label: documentLabel,
            user_id: user.user_id,
          },
        },
        {
          onSuccess: () => onRequestSent(name.trim(), relationship || null),
          onError: () => setError("The request wasn't sent. Try again."),
        },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "The contact wasn't saved. Try again.";
      setError(message);
    }
  };

  return (
    <ModalStepLayout
      sidebar={sidebar}
      title={title}
      description={description}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" disabled={!canSend || isBusy} onClick={handleSend}>
          {isBusy ? "Sending..." : "Send"}
        </Button>
      }
    >
      <hr className="border-primary" />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-base font-semibold tracking-[-0.32px]">
            Who has the {documentLabel.toLowerCase()}?
          </p>
          <p className="text-sm text-secondary">
            Add their info and we'll email them a secure link so they can share the document
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="request-name">Name</Label>
            <Input
              id="request-name"
              placeholder="Their full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="request-relationship">Relationship to {personName}</Label>
            <Select value={relationship} onValueChange={setRelationship}>
              <SelectTrigger id="request-relationship">
                <SelectValue placeholder="Select a relationship" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="request-email">Email</Label>
          <Input
            id="request-email"
            type="email"
            placeholder="example@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="rounded-lg border border-primary bg-white">
          <div className="flex items-center gap-2 border-b border-primary px-4 py-3">
            <Mail className="h-4 w-4 text-secondary" aria-hidden="true" />
            <p className="text-sm font-medium">Here's the email we'll send</p>
          </div>
          <div className="px-4 py-4 text-sm text-secondary leading-relaxed">
            <p>
              We're helping {userFirstName} with some paperwork for {personName}'s estate. Elayne
              works with families and attorneys to settle estates, from probate to asset searches,
              keeping it all in one place.
            </p>
            <br />
            <p>
              If you have a copy of {personName}'s {documentLabel.toLowerCase()}, you can add it
              securely to the estate's records using the button below. It takes about a minute, and
              the link expires in 30 days.
            </p>
            <br />
            <p className="font-medium text-link underline">
              Upload {personName}'s {documentLabel.toLowerCase()}
            </p>
            <br />
            <p>
              If you don't have it, reply and let us know. And if you have any questions, we're
              happy to help.
            </p>
          </div>
        </div>

        {error && <FieldError message={error} />}
      </div>
    </ModalStepLayout>
  );
};
